import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Fragment } from "react/jsx-runtime";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { convertCamelCaseToWords, showToast } from "@/modules/common/utils";
import ModalFooter from "@/modules/common/components/modals/modal-footer";
import z from "zod";
import FieldRenderer from "./field-renderer";
import useStatusPropertyFields from "@/modules/property-tracks/hooks/property-stage/status-property-fields.hook";
import {
  ACCEPTED_TRACK_FILE_TYPES,
  formatFormData,
  PROPERTY_TRACKS,
} from "@/modules/property-tracks/utils";
import { TrackStatusFormField } from "@/modules/property-tracks/dtos/property-stage";
import {
  TRACK_DOCUMENT_TYPES,
  TrackDocument,
  TrackDocumentType,
} from "@/modules/property-tracks/types";
import { TOAST_IDS, ToastType, UploadedFile } from "@/modules/common/types";
import { useCallback, useEffect, useState } from "react";
import useAmenitiesFilterOptions from "@/modules/common/hooks/lookups/amenities-filter-options.hook";
import { useUpdateTrackStatus } from "@/modules/property-tracks/hooks/property-stage/update-track-status.hook";
import { useQueryClient } from "@tanstack/react-query";
import useTrackDocumentTypes from "@/modules/common/hooks/document-types.hook";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";

interface StatusPropertyFieldsProps {
  trackUId: string;
  statusId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  resetDefaultStatus: () => void;
  trackTypeId?: number;
  trackTypeName?: string;
}

const StatusPropertyFields = ({
  statusId,
  open,
  setOpen,
  trackUId,
  resetDefaultStatus,
  trackTypeId,
  trackTypeName,
}: StatusPropertyFieldsProps) => {
  const theme = useTheme();
  const [files, setFiles] = useState<TrackDocument[]>([]);
  const mutationUpdateTrackStatus = useUpdateTrackStatus();
  const queryClient = useQueryClient();

  const {
    schema,
    formStructure,
    docsRequired,
    amenitiesRequired,
    defaultValues,
  } = useStatusPropertyFields({ statusId });
  const handleFile = useCallback((newFile: TrackDocument) => {
    setFiles((prev) => [...prev, newFile]);
  }, []);

  const handleDeleteFile = useCallback(
    (fileToDelete: UploadedFile, documentType: TrackDocumentType) => {
      setFiles((prevFiles) => {
        const updatedFiles = prevFiles.filter(
          (file) =>
            file.file.name !== fileToDelete.name ||
            file.documentType !== documentType,
        );
        return updatedFiles.length > 0 ? updatedFiles : [];
      });
    },
    [],
  );

  const { amenities } = useAmenitiesFilterOptions();

  const visibleAmenities =
    trackTypeName === PROPERTY_TRACKS.HOSPITALITY
      ? amenities
      : amenities?.filter((amenity) => amenity.enablePropertyManagement);

  if (!schema) return <></>;
  const {
    control,
    handleSubmit,
    setValue,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues,
  });

  // hospitalityType is a dependent of rentalTag: it is injected into the same
  // section as rentalTag and shown only when rentalTag is set to hospitality.
  const rentalTagSection = formStructure.find((section) =>
    section.fields.some((field) => field.name === "rentalTag"),
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const rentalTagValue = useWatch({
    control,
    name: rentalTagSection
      ? `${rentalTagSection.fieldLevel}.rentalTag`
      : "rentalTag",
  });

  const isHospitality =
    (typeof rentalTagValue === "string"
      ? rentalTagValue
      : rentalTagValue?.value) === "hospitality";

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!isHospitality && rentalTagSection) {
      setValue(
        `${rentalTagSection.fieldLevel}.hospitalityType` as never,
        undefined as never,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHospitality]);

  const hospitalityTypeField = rentalTagSection
    ? ({
        name: "hospitalityType",
        uid: "synthetic-hospitalityType",
        label: null,
        isDropDown: false,
        fieldLevel: rentalTagSection.fieldLevel,
        fieldType: null,
        isRequired: true,
      } as unknown as TrackStatusFormField)
    : null;

  const onSubmit = (data: FormData) => {
    const formattedData = formatFormData(data);
    mutationUpdateTrackStatus.mutate(
      {
        uid: trackUId ?? "",
        statusId: statusId,
        files,
        amenities: data.amenities ? new Set(data.amenities) : undefined,
        formSections: formattedData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["track-next-actions"],
            refetchType: "all",
          });

          setOpen(false);

          showToast({
            type: ToastType.SUCCESS,
            message: "Track Status updated successfully",
            toastId: TOAST_IDS.UPDATE_TRACK_STATUS_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.UPDATE_TRACK_STATUS_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const trackDocumentTypes = useTrackDocumentTypes(trackTypeId);

  type FormData = z.infer<typeof schema>;

  const handleCloseModal = () => {
    resetDefaultStatus();
    setOpen(false);
  };

  return (
    <EditFieldModal
      open={open}
      setOpen={setOpen}
      header="Enter required information"
      handleCloseModal={handleCloseModal}
      modalFooter={false}
      width={750}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {formStructure.map((section) => (
              <Fragment key={section.fieldLevel}>
                {section.fields.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={
                        theme.nawy.typography.english.variants.body.md.semibold
                      }
                    >
                      {convertCamelCaseToWords(section.fieldLevel)}
                    </Typography>

                    {section.fields.map((field) => (
                      <FieldRenderer
                        key={field.uid}
                        field={field}
                        section={section}
                        control={control}
                      />
                    ))}

                    {section.fieldLevel === rentalTagSection?.fieldLevel &&
                      isHospitality &&
                      hospitalityTypeField && (
                        <FieldRenderer
                          key={hospitalityTypeField.uid}
                          field={hospitalityTypeField}
                          section={section}
                          control={control}
                        />
                      )}
                  </Box>
                )}
              </Fragment>
            ))}

            {docsRequired && (
              <Fragment>
                <Typography
                  sx={theme.nawy.typography.english.variants.body.md.semibold}
                >
                  Upload Documents
                </Typography>

                {trackDocumentTypes.map((value) => (
                  <FileUploadButton
                    label={TRACK_DOCUMENT_TYPES[value]}
                    setFiles={(file) => {
                      handleFile({
                        documentType: value,
                        file: file[0],
                      } as TrackDocument);
                    }}
                    acceptedFileTypes={ACCEPTED_TRACK_FILE_TYPES[value]}
                    onDeleteFile={(file) => {
                      handleDeleteFile(file, value);
                    }}
                    maxFiles={1}
                  />
                ))}
              </Fragment>
            )}

            {amenitiesRequired && (
              <Fragment>
                <Typography
                  sx={theme.nawy.typography.english.variants.body.md.semibold}
                >
                  Amenities
                </Typography>

                <Controller
                  control={control}
                  name={"amenities"}
                  render={({ field }) => (
                    <Box>
                      <FormGroup>
                        {visibleAmenities?.map((amenity) => (
                          <FormControlLabel
                            key={amenity.id}
                            control={
                              <Checkbox
                                checked={field.value?.has(amenity.id) || false}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  const newValue = new Set(field.value ?? []);
                                  if (checked) {
                                    newValue.add(amenity.id);
                                  } else {
                                    newValue.delete(amenity.id);
                                  }
                                  field.onChange(newValue);
                                }}
                              />
                            }
                            label={amenity.name}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  )}
                />
              </Fragment>
            )}
          </Box>

          <Box>
            <ModalFooter
              handleClose={handleCloseModal}
              confirmText={"Submit Status Update"}
              isSubmitting={mutationUpdateTrackStatus.isPending}
            />
          </Box>
        </Box>
      </form>
    </EditFieldModal>
  );
};

export default StatusPropertyFields;
